import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, Grid, Typography, Box } from '@mui/material';
import { db } from './auth/firebaseConfig';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';

// Helper to get the current Monday for a given date
function getCurrentMonday(date) {
  const d = new Date(date);
  const day = d.getDay();
  // If today is Monday (day === 1), subtract 0 days
  // If today is Sunday (day === 0), subtract 6 days
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Helper to parse custom date format '10-Jun-2025, 06:01:00 pm'
function parseCustomDate(dateStr) {
  if (!dateStr) return null;
  // Debug: Log the raw input
 
  const [datePart, timePart] = dateStr.split(',').map(s => s.trim());
  if (!datePart || !timePart) return null;
  const [day, monthStr, year] = datePart.split('-').map(s => s.trim());
  const months = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11
  };
  const month = months[monthStr];
  if (month === undefined) return null;
  let [time, ampm] = timePart.split(' ').map(s => s.trim());
  let [hours, minutes, seconds] = time.split(':').map(s => s.trim());
  hours = parseInt(hours, 10);
  if (ampm && ampm.toLowerCase() === 'pm' && hours !== 12) hours += 12;
  if (ampm && ampm.toLowerCase() === 'am' && hours === 12) hours = 0;
  const parsedDate = new Date(year, month, parseInt(day, 10), hours, parseInt(minutes, 10), parseInt(seconds, 10));
  // Debug: Log the parsed date
 
  return parsedDate;
}

const MetricsCharts = ({ boardId, projectId }) => {
  const [metricsData, setMetricsData] = useState({
    leadTime: [],
    cycleTime: [],
    velocity: []
  });

  useEffect(() => {
    const fetchMetricsData = async () => {
      try {
        // Fetch cards for the board
        const cardsQuery = query(
          collection(db, 'cards'),
          where('boardId', '==', boardId)
        );
        const cardsSnapshot = await getDocs(cardsQuery);
        const cards = cardsSnapshot.docs.map(doc => doc.data());

       

        // Calculate metrics
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));

        // Lead Time (time from creation to completion)
        const leadTimeData = cards
          .filter(card => (card.cardStatus === 'Done' || card.cardStatus === 'Closed') && card.cardCreationDate && card.cardUpdatedOn)
          .map(card => {
            // Use custom parser for cardCreationDate
            const creationDate = parseCustomDate(card.cardCreationDate) || new Date(card.cardCreationDate);
            const completionDate = new Date(card.cardUpdatedOn);
            const value = (completionDate - creationDate) / (1000 * 60 * 60 * 24); // Convert to days
            // Debug: Log lead time calculation

            return {
              date: completionDate.getFullYear() + '-' + String(completionDate.getMonth() + 1).padStart(2, '0') + '-' + String(completionDate.getDate()).padStart(2, '0'),
              value
            };
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        // Cycle Time (time from start to completion)
        const cycleTimeData = cards
          .filter(card => (card.cardStatus === 'Done' || card.cardStatus === 'Closed') && (card.assignDate || card.cardCreationDate) && card.cardUpdatedOn)
          .map(card => {
            // Use custom parser for assignDate or fallback to cardCreationDate
            const startDate = parseCustomDate(card.assignDate || card.cardCreationDate) || new Date(card.assignDate || card.cardCreationDate);
            const completionDate = new Date(card.cardUpdatedOn);
            const value = (completionDate - startDate) / (1000 * 60 * 60 * 24); // Convert to days
            // Debug: Log cycle time calculation
           
            return {
              date: completionDate.getFullYear() + '-' + String(completionDate.getMonth() + 1).padStart(2, '0') + '-' + String(completionDate.getDate()).padStart(2, '0'),
              value
            };
          })
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        // Velocity (number of completed cards per week)
        const velocityData = [];
        const thisMonday = getCurrentMonday(now);
        for (let i = 3; i >= 0; i--) {
          const weekStart = new Date(thisMonday.getTime() - (i * 7 * 24 * 60 * 60 * 1000));
          const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
          const completedCards = cards.filter(card => {
            const done = card.cardStatus === 'Done' || card.cardStatus === 'Closed';
            const updated = card.cardUpdatedOn;
            const updatedDate = updated ? new Date(card.cardUpdatedOn) : null;
            return done && updatedDate && updatedDate >= weekStart && updatedDate < weekEnd;
          });
          velocityData.push({
            date: weekStart.getFullYear() + '-' + String(weekStart.getMonth() + 1).padStart(2, '0') + '-' + String(weekStart.getDate()).padStart(2, '0'),
            value: completedCards.length
          });
        }

        setMetricsData({
          leadTime: leadTimeData,
          cycleTime: cycleTimeData,
          velocity: velocityData
        });
      } catch (error) {
        
      }
    };

    if (boardId) {
      fetchMetricsData();
    }
  }, [boardId]);

  const getLeadTimeOptions = () => ({
    title: {
      text: 'Lead Time (Days)',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c} days'
    },
    xAxis: {
      type: 'category',
      data: metricsData.leadTime.map(item => item.date)
    },
    yAxis: {
      type: 'value',
      name: 'Days'
    },
    series: [{
      data: metricsData.leadTime.map(item => item.value),
      type: 'line',
      smooth: true,
      areaStyle: {
        opacity: 0.3
      }
    }]
  });

  const getCycleTimeOptions = () => ({
    title: {
      text: 'Cycle Time (Days)',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c} days'
    },
    xAxis: {
      type: 'category',
      data: metricsData.cycleTime.map(item => item.date)
    },
    yAxis: {
      type: 'value',
      name: 'Days'
    },
    series: [{
      data: metricsData.cycleTime.map(item => item.value),
      type: 'line',
      smooth: true,
      areaStyle: {
        opacity: 0.3
      }
    }]
  });

  const getVelocityOptions = () => ({
    title: {
      text: 'Velocity (Cards per Week)',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      formatter: '{b}: {c} cards'
    },
    xAxis: {
      type: 'category',
      data: metricsData.velocity.map(item => item.date)
    },
    yAxis: {
      type: 'value',
      name: 'Cards'
    },
    series: [{
      data: metricsData.velocity.map(item => item.value),
      type: 'bar',
      barWidth: '60%'
    }]
  });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card sx={{ p: 2, height: '100%' }}>
          <ReactECharts
            option={getLeadTimeOptions()}
            style={{ height: '300px' }}
          />
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card sx={{ p: 2, height: '100%' }}>
          <ReactECharts
            option={getCycleTimeOptions()}
            style={{ height: '300px' }}
          />
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card sx={{ p: 2, height: '100%' }}>
          <ReactECharts
            option={getVelocityOptions()}
            style={{ height: '300px' }}
          />
        </Card>
      </Grid>
    </Grid>
  );
};

export default MetricsCharts; 